import { FileIcon, PlusIcon } from "lucide-react";
import { useNavigate } from "react-router";

import { Column, EmptyState, Text } from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { SignupVariant } from "@/types";

export const SignupVariantsTable = ({
  signupVariants,
}: {
  signupVariants: SignupVariant[];
}) => {
  const navigate = useNavigate();

  const handleRowClick = (signupVariant: SignupVariant) => {
    mixpanelUtils.trackRowClick("Signup variant", MIXPANEL_SCREENS.SIGNUP_VARIANTS);
    void navigate(`/tools/signup-variants/${signupVariant.id.toString()}`);
  };

  return (
    <Column className="overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[40%]">
                <Text variant="header">Name</Text>
              </TableHead>
              <TableHead className="w-[30%]">
                <Text variant="header">Type</Text>
              </TableHead>
              <TableHead className="w-[30%]">
                <Text variant="header">Hash</Text>
              </TableHead>
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table className="table-fixed">
          <TableBody>
            {signupVariants.length === 0 ? (
              <TableRow>
                <TableCell className="p-0" colSpan={3}>
                  <EmptyState
                    className="bg-slate-50 py-14"
                    description="No signup variants were found. Add a signup variant to get started!"
                    handlePrimaryClick={() => {
                      mixpanelUtils.trackButtonClick(
                        "Create signup variant",
                        MIXPANEL_SCREENS.SIGNUP_VARIANTS,
                      );
                      void navigate("/tools/signup-variants/create");
                    }}
                    Icon={FileIcon}
                    PrimaryButtonIcon={PlusIcon}
                    primaryButtonName="Create signup variant"
                    title="No signup variants found"
                  />
                </TableCell>
              </TableRow>
            ) : (
              signupVariants.map((signupVariant) => (
                <TableRow
                  className="cursor-pointer"
                  key={signupVariant.id}
                  onClick={() => {
                    handleRowClick(signupVariant);
                  }}
                >
                  <TableCell className="w-[40%]">
                    <Text truncate>{signupVariant.name}</Text>
                  </TableCell>
                  <TableCell className="w-[30%]">
                    <Text truncate variant="secondary">
                      {/* Payload can be `null`. */}
                      {signupVariant.payload?.type === "jobSpecific"
                        ? "Job Specific"
                        : "Generic"}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[30%]">
                    <Text truncate variant="secondary">
                      {signupVariant.hash}
                    </Text>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Column>
  );
};
